import { useEffect, useState, useRef, useMemo } from 'react'
import { TextToggle } from '../ui-components/TextToggle'
import Button from '../ui-components/Button'
import { useQuery } from '@tanstack/react-query'
import { EventNames, UNITS } from '../../types/misc.types'
import { fetchRollingRankData, fetchTrailingRankData } from '../../repository/MutualFundDetail'
import { useMetaDataContext } from '../../state-management/contexts/MetaDataContext'
import { convertEpochToDate, convertUnitAndValue, toPascalCaseWithSpaces } from '../../utils/utils'
import {
  EliteScoreTrends,
  RanksData,
  RanksDatum,
  WaterfallSections,
  WaterfallValues
} from '../../types/mutual-fund-detail.types'
import Tabs from '../ui-components/Tabs'
import { performanceTagMetaData } from '../../constants'
import MFRankSkeleton from '../skeletons/MFRankSkeleton'
import RankGraphUnavailable from './RankGraphUnavailable'
import useAlternateISIN from '../../hooks/useAlternateISIN'
import { trackEvent } from '../../utils/appBridge'
import { sortTenures } from '../../utils/mutualFundDetail'
import VaulDrawer from '../BottomSheetComponents/VaulDrawer'
import { AboutThisRankContent } from '../BottomSheetComponents/AboutThisRankContent'
import { LockedRankSection } from '../error-states/LockedRankSection'
import Remark from '../ui-components/Remark'
import NewRankGraph from './NewRankGraph'
import useWindowSize from '../../hooks/useWindowSize'

type RankList = RanksDatum['returnsRankList']

type RankSectionPropTypes = {
  ranksType: 'ROLLING' | 'TRAILING'
}

export default function RankSection({ ranksType }: RankSectionPropTypes) {
  const { data: metadata, directFundName, isin, isUserElite } = useMetaDataContext()
  const [currentToggleOption, setCurrentToggleOption] = useState('')
  const [toggleOptions, setToggleOptions] = useState<string[]>([])
  const [rankData, setRankData] = useState<RankList>([])
  const [selectedTabId, setSelectedTabId] = useState<string>('6M')
  const [filteredData, setFilteredData] = useState<RankList>([])
  const [showLabels, setShowLabels] = useState(false)
  const currentRankRef = useRef<HTMLSpanElement>(null)
  const monthRef = useRef<HTMLDivElement>(null)
  const rankHistoryRef = useRef<HTMLDivElement>(null)
  const [openBottomSheet, setOpenBottomSheet] = useState(false)
  const [bottomSheetContentKey, setBottomSheetContentKey] = useState<any>(null)

  const { alternateISIN, config } = useAlternateISIN(WaterfallSections.RANK)
  const { width } = useWindowSize()

  const isInteractionEventLocked = useRef<boolean>(false)

  const ScreenName = useMemo(() => {
    return ranksType === 'ROLLING' ? `MF_L2_Rolling_Returns_Rank` : `MF_L2_Trailing_Returns_Rank`
  }, [ranksType])

  const rankMap = useRef<{ [x: string]: RanksDatum }>({})
  const { data, isLoading, isError } = useQuery<any, Error>({
    queryKey: [ranksType === 'ROLLING' ? 'rollingReturnsRank' : 'trailingReturnsRank', alternateISIN],
    queryFn: () => {
      return ranksType === 'ROLLING' ? fetchRollingRankData(alternateISIN) : fetchTrailingRankData(alternateISIN)
    },
    enabled: isUserElite && !!alternateISIN,
    retry: 1,
    refetchOnWindowFocus: false
  })

  const renderBottomSheetContent = () => {
    switch (bottomSheetContentKey) {
      case 'aboutRank':
        return (
          <AboutThisRankContent
            onDismiss={() => setOpenBottomSheet((open) => !open)}
            type={ranksType}
            ScreenName={ScreenName}
          />
        )
      default:
        return null
    }
  }

  const getSortedData = (dataList: RankList) => {
    let scoreHistoryList: RankList = JSON.parse(JSON.stringify(dataList || []))
    const sortedData = scoreHistoryList?.sort((a, b) => (b?.x_value ?? 0) - (a?.x_value ?? 0))
    if (scoreHistoryList?.length < 25 && scoreHistoryList?.length > 0) {
      let lastPointMonthDate = new Date(sortedData?.slice(-1)?.[0]?.x_value)
      let remainingLength = 25 - sortedData?.length
      while (remainingLength > 0) {
        const lastPointMonthDateCopy = new Date(JSON.parse(JSON.stringify(lastPointMonthDate))) as Date
        const prevMonth = new Date(lastPointMonthDateCopy?.setMonth(lastPointMonthDateCopy?.getMonth() - 1))
        const prevMonthTime = prevMonth?.getTime()
        const prevMothPoint: RankList[0] = {
          x_value: prevMonthTime,
          y_value: 40,
          label: -1,
          tag: 'BAD'
        }
        sortedData?.push(prevMothPoint)
        lastPointMonthDate = new Date(prevMonthTime)
        remainingLength--
      }
    }
    return sortedData?.sort((a, b) => (a?.x_value ?? 0) - (b?.x_value ?? 0))
  }

  useEffect(() => {
    if (data?.data) {
      const dataCopy: RanksData = data?.data
      let ranksData: RanksDatum[] = dataCopy?.rollingReturnsRanks ?? dataCopy?.trailingReturnsRanks ?? []
      const firstRanksData = ranksData?.[0]

      const rankList = firstRanksData?.returnsRankList || []
      const sortedData = getSortedData(rankList)
      setRankData(() => sortedData)
      setFilteredData(() => sortedData?.slice(-7)) // Default to 7 values
      const tenures: string[] = []
      const mappedTenures =
        ranksData?.map((datum) => {
          return datum?.tenure
        }) ?? []
      const sortedTenures = sortTenures(mappedTenures)
      sortedTenures?.forEach((datum) => {
        const tenure = `${convertUnitAndValue({
          value: datum?.value ?? 0,
          unit: datum?.period ?? UNITS.MONTH,
          format: {
            casing: 'PASCAL',
            type: 'SHORT'
          }
        })} ${ranksType === 'ROLLING' ? 'Rolling' : 'Trailing'} Returns`
        tenures?.push(tenure)
        const uniqueTenures = [...new Set(tenures)]
        setToggleOptions(() => uniqueTenures)
        setCurrentToggleOption(() => uniqueTenures?.[0])
      })
      ranksData?.forEach((datum) => {
        const tenure = `${convertUnitAndValue({
          value: datum?.tenure?.value ?? 0,
          unit: datum?.tenure?.period ?? UNITS.MONTH,
          format: {
            casing: 'PASCAL',
            type: 'SHORT'
          }
        })} ${ranksType === 'ROLLING' ? 'Rolling' : 'Trailing'} Returns`
        if (rankMap && rankMap?.current)
          rankMap.current[`${tenure}`] = {
            ...datum,
            returnsRankList: getSortedData(datum?.returnsRankList) ?? []
          }
      })
    }
  }, [data])

  const categoryCount = useMemo(() => {
    return data?.data?.categoryCount
  }, [data])

  const lastRankDatum = useMemo(() => {
    return filteredData?.slice(-1)?.[0] ?? null
  }, [filteredData])

  useEffect(() => {
    const sliceCount =
      {
        '6M': 7,
        '1Y': 13,
        '2Y': 25
      }[selectedTabId] ?? 0
    if (currentToggleOption && rankMap?.current && selectedTabId) {
      const updatedData = rankMap.current[currentToggleOption]?.returnsRankList?.slice(-sliceCount) || []
      setFilteredData(updatedData)
    }
  }, [currentToggleOption, selectedTabId])

  const convertData = (dataList: RankList) => {
    const sampleData: {
      month: number
      value: number
      label: string
      tag: `${EliteScoreTrends}`
      isVisible: boolean
    }[] = []
    dataList.forEach((data) => {
      sampleData.push({
        month: data.x_value,
        value: data.y_value,
        label: convertUnitAndValue({
          value: data.label,
          unit: UNITS.PERCENTAGE
        }),
        tag: data.tag,
        isVisible: data?.label !== -1
      })
    })
    return sampleData
  }

  const tabsData = [
    { id: '6M', label: '6M' },
    { id: '1Y', label: '1Y' },
    { id: '2Y', label: '2Y' }
  ].filter((tab, index) => index < rankData?.length / 7)

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        eventId: ranksType === 'ROLLING' ? 'rolling_returns' : 'trailing_returns',
        ScreenName,
        fundName: isin,
        screenType: 'MF_L2'
      }
    })
  }, [])

  const getTodayLabel = () => {
    if (ranksType === 'ROLLING') {
      const lastPointDateOfFilteredData = filteredData?.slice(-1)?.[0]?.x_value
      return convertEpochToDate(lastPointDateOfFilteredData, '%b %Y')
    } else {
      return 'Today'
    }
  }

  if (!isUserElite) return <LockedRankSection ranksType={ranksType} />

  if (isLoading) return <MFRankSkeleton />

  return (
    <div className='flex flex-col w-full pb-[24px]'>
      {config === WaterfallValues.YES_DIRECT && (
        <div className='pb-6 w-full '>
          <Remark lineHeight={19} remarkClassName='py-3' isElite={true}>
            Showing Rank Trend for <span className='font-[500]'> Direct Growth Plan </span>
          </Remark>
        </div>
      )}

      <div className={`flex justify-between gap-1 ${isError ? 'pb-4' : ''}`}>
        <div className='flex flex-col flex-1'>
          <div className='text-black font-[500] pb-[2px]'>
            Rank in {toPascalCaseWithSpaces(metadata?.fundDetails?.category ?? 'NA')}
          </div>
          {toggleOptions.length !== 0 && (
            <div className={`${width < 375 ? 'flex flex-col gap-1' : 'flex gap-1'} text-[#3D3D3D] pb-4`}>
              <div>Based on</div>
              <div>
                <TextToggle
                  currentToggleOption={currentToggleOption}
                  toggleOptions={toggleOptions}
                  onToggleClick={(id) => {
                    trackEvent({
                      eventName: EventNames.GraphInteraction,
                      payload: {
                        eventId: `returns_toggle`,
                        responseName: id?.split(' ')?.[0],
                        ScreenName,
                        fundName: isin,
                        screenType: 'MF_L2'
                      }
                    })
                    setCurrentToggleOption(() => id)
                  }}
                  toggleColor='#3E6CE3'
                  toggleClassName='font-[500]'
                />
              </div>
            </div>
          )}
        </div>
        <div className='flex flex-col'>
          <div className='text-end'>
            {categoryCount ? (
              <>
                <span ref={currentRankRef} className='text-black text-[20px] font-[700]'></span>
                <span className='text-[#6A6E6E] text-[16px] font-[400]'>/{categoryCount}</span>
              </>
            ) : (
              <span className='text-black text-[20px] font-[700] animate-fade-150'>NA</span>
            )}
          </div>
          {lastRankDatum && (
            <div
              style={{ background: performanceTagMetaData?.[lastRankDatum?.tag]?.color }}
              className='text-black font-[500] text-[13px] text-end rounded-[8px] px-[6px]'
            >
              {performanceTagMetaData?.[lastRankDatum?.tag]?.label}{' '}
              <span className='opacity-60 font-[400]'> in {convertEpochToDate(lastRankDatum?.x_value, '%B')}</span>
            </div>
          )}
        </div>
      </div>
      <hr aria-hidden='true' className='bg-[#EBF2F2] opacity-70' />

      <div className='flex flex-col'>
        <h3
          ref={rankHistoryRef}
          className={`text-[#6A6E6E] pt-4 pb-2 pl-1 text-13 font-[400] m-0 ${isError || filteredData?.length <= 0 ? '' : 'mb-[-32px]'}`}
        >
          Rank Trend
        </h3>
        <div>
          {filteredData?.length > 0 ? (
            <NewRankGraph
              data={convertData(filteredData)}
              ranksType={ranksType}
              showLabels={showLabels}
              categoryCount={categoryCount}
              onRankChange={(rank: string | number, date: any, hideRankSection: boolean) => {
                if (currentRankRef && currentRankRef?.current) currentRankRef.current.innerHTML = `${rank}`
                if (monthRef && monthRef?.current)
                  monthRef.current.innerHTML = date === 'Today' ? getTodayLabel() : convertEpochToDate(date, '%b %Y')
                const isDateToday = date === 'Today'
                if (!isInteractionEventLocked?.current && !isDateToday) {
                  trackEvent({
                    eventName: EventNames.GraphInteraction,
                    payload: {
                      eventId: 'slider_move',
                      responseName: `${selectedTabId}`,
                      ScreenName,
                      fundName: isin,
                      screenType: 'MF_L2'
                    }
                  })
                  isInteractionEventLocked.current = true
                }
                if (rankHistoryRef && rankHistoryRef?.current)
                  rankHistoryRef.current.style.visibility = hideRankSection ? 'hidden' : 'visible'
              }}
            />
          ) : (
            <RankGraphUnavailable />
          )}
        </div>
      </div>
      {filteredData?.length > 0 && (
        <div className='flex gap-3 pt-4'>
          <div className='flex-1'>
            <Tabs
              isFullWidth
              tabsData={tabsData}
              onTabClick={(tabId: any) => {
                trackEvent({
                  eventName: EventNames.GraphInteraction,
                  payload: {
                    eventId: 'graph_period',
                    responseName: tabId?.split(' ')?.[0],
                    ScreenName,
                    fundName: isin,
                    screenType: 'MF_L2'
                  }
                })
                setSelectedTabId(tabId)
              }}
              selectedTabId={selectedTabId}
            />
          </div>
          <div className='w-[85px]'>
            <Button isFullWidth onClick={() => null} buttonClassName='outline-none min-h-9'>
              <TextToggle
                currentToggleOption={'Rank'}
                toggleOptions={['Rank', 'Returns']}
                onToggleClick={() => {
                  trackEvent({
                    eventName: EventNames.GraphInteraction,
                    payload: {
                      eventId: 'graph_toggle',
                      responseName: showLabels ? 'Rank' : 'Returns',
                      ScreenName,
                      fundName: isin,
                      screenType: 'MF_L2'
                    }
                  })
                  setShowLabels(!showLabels)
                }}
                toggleColor='#232323'
                toggleClassName='font-[500]'
              />
            </Button>
          </div>
          {/* <div className='w-[38px]'>
            <Button onClick={() => null} buttonClassName='min-w-[20px] w-[38px] min-h-9'>
              <img src={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/help-icon.svg`} />
            </Button>
          </div> */}
        </div>
      )}
      <div className='pt-6 text-[#6A6E6E] text-[13px]'>
        {ranksType === 'TRAILING' ? ' Updates daily at 10:00 AM' : 'Updates every month'}
      </div>
      {/* <div className='pt-2'>
        <TextAndInfo
          text='About this rank'
          textClassName='!text-[#6A6E6E] !text-[13px] !font-[400]'
          onInfoClick={(event) => {
            event.stopPropagation()
            setBottomSheetContentKey('aboutRank')
            setOpenBottomSheet((open) => !open)
          }}
        />
      </div> */}

      <VaulDrawer open={openBottomSheet} setOpen={setOpenBottomSheet}>
        {renderBottomSheetContent()}
      </VaulDrawer>
    </div>
  )
}
